/**
 * Where a page sits once a question has been answered.
 *
 * Answering prints something the learner has to read — the tick or the cross, the
 * solution, the explanation — and it is printed *under* the question, on the same
 * continuous page. Two ways to get this wrong, and the reader has done both:
 *
 *  - **Bringing the next question into view.** The page moves on without the reader,
 *    and what they answered scrolls off the top. The verdict exists, it is correct,
 *    and it is never seen: the learner is asked to trust a page that told them
 *    nothing. This is the behaviour that was reported.
 *  - **Never moving.** On a question longer than the frame the verdict lands below
 *    the fold, so the press appears to have done nothing at all, and the reader
 *    presses the control again.
 *
 * So the page moves the *least it can* while putting the answered block's own end on
 * screen — not the next block's start. When the verdict is already visible the page
 * does not move; when it is not, the page goes far enough down that the block's last
 * line is in view and no further. The next question follows it on the page, which is
 * how a book reads: there is no advance control anywhere in one.
 *
 * One assumption, and it is what keeps this a policy rather than a scrollbar: the
 * reader *answered* this block, so its top is on screen and the page only ever moves
 * down. Nothing here scrolls back up to a block the reader has scrolled past.
 *
 * Pure arithmetic over measured boxes — no view, no clock — so the rule can be tested
 * without a frame, a font or an animation.
 */

export type VerdictViewInput = {
  /** The answered block's top, in the scroller's own content coordinates. */
  blockTop: number
  /** Its measured height, *as it is now* — after the verdict was printed into it. */
  blockHeight: number
  /** The scroller's visible height. */
  viewportHeight: number
  /** Where the scroller is now. */
  current: number
  /** How much of the page's own padding should stay under the last line. */
  padding?: number
  /** The furthest the scroller may go: the content's height minus the frame's. */
  maxTop: number
}

/**
 * The page's offset after an answer.
 *
 * Returns `current` when the block's end is already on screen, so an answer that
 * changes nothing about what the reader can see does not move the page under them.
 */
export function verdictScrollTop({
  blockTop,
  blockHeight,
  viewportHeight,
  current,
  padding = 0,
  maxTop,
}: VerdictViewInput): number {
  /*
   * Where the block's last line lands at the bottom of the frame, with the page's
   * own padding kept under it so the verdict is not flush against the edge.
   */
  const desired = blockTop + blockHeight - (viewportHeight - padding)
  // Already there, or further down than it needs to be: the verdict is visible on
  // screen right now, and a page that moves anyway is a page that moves for its own
  // reasons rather than the reader's.
  if (desired <= current) return current
  // Never past the end of the content — a scroller asked for more than it has
  // bounces back on some platforms and simply does nothing on others.
  return Math.max(current, Math.min(desired, maxTop))
}
